import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../lib/anim";

const PHOTOS = [
  { src: "/photo-hero.webp", caption: "Amaan with the Lifeline Primary Care team at the hygiene kit distribution table." },
  { src: "/photo-kits.webp", caption: "Dozens of assembled hygiene kits — each with a health-education handout — ready for patients." },
  { src: "/photo-community1.webp", caption: "Walking community members through the personal-hygiene handout." },
  { src: "/photo-doctor.webp", caption: "Coordinating distribution logistics with a Lifeline physician." },
  { src: "/photo-community2.webp", caption: "A patient completing the AMCares mini-survey to shape future kit needs." },
];

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + PHOTOS.length) % PHOTOS.length); }, []);
  const next = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % PHOTOS.length); }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <section id="gallery" data-testid="gallery" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-brand-blue font-bold mb-4">On the Ground</p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-brand-ink max-w-2xl leading-[1.02]">
            Distribution Day at Lifeline Primary Care.
          </h2>
          <p className="text-brand-ink/55 max-w-sm md:text-right">
            Real moments from our hygiene-kit distribution — where preventive care meets community, one family at a time.
          </p>
        </div>
      </Reveal>

      {/* Editorial asymmetric grid */}
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[240px] gap-4 md:gap-5">
        {PHOTOS.map((p, i) => {
          const spans = [
            "col-span-2 row-span-2",
            "col-span-2 lg:col-span-1 row-span-1",
            "col-span-1 row-span-1",
            "col-span-1 lg:col-span-2 row-span-1",
            "col-span-2 lg:col-span-1 row-span-2",
          ];
          return (
            <Reveal key={i} delay={i * 0.06} className={spans[i]}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setIndex(i)}
                className="group relative w-full h-full rounded-2xl overflow-hidden border hairline shadow-sm"
              >
                <img src={p.src} alt={p.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute bottom-0 left-0 right-0 p-4 text-left text-white text-sm translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {p.caption}
                </span>
              </button>
            </Reveal>
          );
        })}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-brand-ink/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          >
            <button data-testid="lightbox-close" onClick={close} className="absolute top-5 right-5 text-white/80 hover:text-white p-2">
              <X className="h-7 w-7" />
            </button>
            <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 md:left-8 text-white/70 hover:text-white p-2">
              <ChevronLeft className="h-8 w-8 md:h-10 md:w-10" />
            </button>
            <button data-testid="lightbox-next" onClick={next} className="absolute right-3 md:right-8 text-white/70 hover:text-white p-2">
              <ChevronRight className="h-8 w-8 md:h-10 md:w-10" />
            </button>

            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img src={PHOTOS[index].src} alt={PHOTOS[index].caption} className="w-full max-h-[75vh] object-contain rounded-2xl" />
              <p className="mt-4 text-center text-white/80 text-sm md:text-base">{PHOTOS[index].caption}</p>
              <p className="mt-1 text-center text-white/40 text-xs font-mono">{index + 1} / {PHOTOS.length}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
